import React, { useEffect, useState } from 'react'
import { Form, FormGroup, FormLabel, FormControl, Button, Row, Col, Container } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { register } from '../actions/userActions'
import FormContainer from '../components/FormContainer'
import Loader from '../components/Loader'
import Message from '../components/Message'
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer, toast } from 'react-toastify';
import { userRegisterReset } from '../reducers/userReducers'
import { Turnstile } from '@marsidev/react-turnstile'
import { COLORS } from '../constants/colors';


function RegisterScreen() {
  
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [message, setMessage] = useState('')
  const [turnstileToken, setTurnstileToken] = useState(null)

  const location = useLocation()
  const redirect = location.search ? location.search.split('=')[1] : '/'

  const {
    userReducers: { userRegisterLoading, userRegisterError, userRegisterSuccess, user }
  } = useSelector((state) => state)

  const dispatch = useDispatch()

  const copper = COLORS.COPPER;
  const sageGreen = COLORS.SAGE_GREEN;
  const warmConcrete = COLORS.WARM_CONCRETE;

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasNoSpaces = !/\s/.test(password);
    const hasNoCommonPatterns = !/(password|123456|qwerty|admin)/i.test(password);
    
    if (password.length < minLength) {
      return 'Password must be at least 8 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    if (!hasNoSpaces) {
      return 'Password cannot contain spaces';
    }
    if (!hasNoCommonPatterns) {
      return 'Password cannot contain common patterns or words';
    }
    return '';
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const submitHandler = (e) => {
    e.preventDefault()
    
    const emailError = validateEmail(email);
    if (emailError) {
      setMessage(emailError);
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setMessage(passwordError);
      return;
    }

    if (password !== passwordConfirm) {
      setMessage('Passwords do not match!');
      return;
    }

    if (!turnstileToken) {
      setMessage('Please complete the security check');
      return;
    }

    dispatch(register(name, email, password, turnstileToken));
  }

  useEffect(() => {
      if (userRegisterLoading) {
          toast.info("Response is pending...", {
              position: "top-right",
              autoClose: false,
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnHover: false,
              draggable: false,
              theme: "dark",
          })
      }
      
      if (!userRegisterLoading) {
          if (userRegisterSuccess){
              toast.dismiss()
              let success = async () => {
                  dispatch(userRegisterReset())
                  toast.success(`Hi ${name}, please go to your email: ${email} inbox and click on the received activation link to confirm and complete the registration. The verification link is valid for 1 hour. Note: Check your spam folder if you don't see it in your inbox.`, {
                      position: "top-center",
                      autoClose: 8000,
                      hideProgressBar: false,
                      closeOnClick: true,
                      pauseOnHover: true,
                      draggable: true,
                      theme: "dark",
                  })
              }
              success();
          }

          if (userRegisterError) {
              toast.dismiss()
              toast.error(userRegisterError, {
                  position: "top-right",
                  autoClose: 5000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true,
                  theme: "dark",
              })
          }
      }
  }, [userRegisterLoading, userRegisterSuccess, dispatch, userRegisterError, name, email])

  const navigate = useNavigate()

  useEffect(() => {
    if(user){
      navigate('/')
    }
  }, [navigate, user])

  return (
    <div className="min-vh-100 py-5" style={{ backgroundColor: warmConcrete }}>
      <Container>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
        <FormContainer>
          <h1>Sign Up</h1>

          {message && <Message variant='danger'>{message}</Message>}
          {userRegisterLoading && <Loader />}
          <Form onSubmit={submitHandler}>
            <FormGroup controlId='name'>
              <FormLabel className='my-2 fw-semibold'>Full Name</FormLabel>
              <FormControl
                  required
                  type='name'
                  placeholder='Enter Full Name'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
              />
            </FormGroup>

            <FormGroup controlId='email'>
              <FormLabel className='my-2 fw-semibold'>Email Address</FormLabel>
              <FormControl
                  required
                  type='email'
                  placeholder='Enter Email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
              />
              <small style={{ fontSize: '0.7em', color: copper }}>
                We'll send you a verification email to activate your account.
              </small>
            </FormGroup>

            <FormGroup controlId='password'>
              <FormLabel className='my-2 fw-semibold'>Password</FormLabel>
              <FormControl
                  required
                  type='password'
                  placeholder='Enter Password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
              />
              <small style={{ fontSize: '0.7em', color: copper }}>
                Password must be at least 8 characters long and contain uppercase, lowercase, numbers, special characters, no spaces, and no common patterns or words.
              </small>
            </FormGroup>

            <FormGroup controlId='passwordConfirm'>
              <FormLabel className='my-2 fw-semibold'>Confirm Password</FormLabel>
              <FormControl
                  required
                  type='password'
                  placeholder='Confirm Password'
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
              />
            </FormGroup>

            <div className="d-flex justify-content-center my-3">
              <Turnstile
                  siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onError={() => {
                      setMessage('Security check failed. Please try again.');
                      setTurnstileToken(null);
                  }}
              />
            </div>

            <Button 
              type='submit' 
              style={{ backgroundColor: sageGreen, borderColor: warmConcrete }}
              className='my-2 w-100'
              disabled={!turnstileToken}
            >
              Register            
            </Button>
          </Form>

          <Row className='py-3'>
            <Col>
              Already have an Account? <Link to={redirect ? `/login?redirect=${redirect}` : '/login'} className='link fw-semibold'>
                Sign in
              </Link>
            </Col>
          </Row>
        </FormContainer>
      </Container>
    </div>
  )
}

export default RegisterScreen
